import type { Event, EventHint, Log } from '@sentry/nextjs'
import { redactAccessCodeFragments } from '@/lib/auth/customer/access-code-fragment'
import { redactLineMarks } from '@/lib/sentry/line-marks'

// Pマーク対応: 個人情報の第三者提供（米国 Sentry サーバーへの送信）を防ぐため、beforeSend で PII をマスクする
const PII_PATTERNS: Array<{ pattern: RegExp; replacement: string }> = [
  // 🔴 並び順に意味がある。**電話番号より先に**当てる。
  //    電話の正規表現は数字の並びを貪欲に拾うので、後に回すと LINE userId や住所の
  //    番地部分だけが先に [phone] へ置き換わり、残りが素のまま外へ出る
  //    （実測: `U0123456789abcdef…` → `U[phone]abcdef[phone]abcdef`）。
  // 🔴 LINE の userId（U + 16進32桁）。Chat 側では伏せていたのに Sentry では素通しだった
  //    （Codex 独立レビュー HIGH-2）。相関が要る場面は hashForCorrelation を使う。
  { pattern: /\bU[0-9a-f]{32}\b/g, replacement: '[line-user-id]' },
  // 🔴 住所（都道府県＋以降の番地まで）。例外本文に `東京都千代田区1-2-3 で失敗` の形で混ざる。
  //    都道府県名から番地までを1つの塊として落とす。都道府県名が無い断片（`中央区1-2-3`）は
  //    地名と判別できないので落とさない（調査の手がかりを消しすぎない側に倒す）。
  {
    pattern:
      /(?:北海道|東京都|(?:京都|大阪)府|[^\s、。]{2,3}県)[^\s、。]{1,40}?\d+[-−ー－]\d+(?:[-−ー－]\d+)?/g,
    replacement: '[address]',
  },
  // 🔴 @ の前は「文字の並びの頭」からだけ照合を始める（`(?<!…)`）。頭を縛らない `+` は、数字・ハイフン・点が
  //    長く続く文（8万文字）で1文字ずらすたびに末尾まで探し直し、1件 4〜7 秒かかった（2026-10-19 実測・
  //    maskPii は全ての文字列が通る）。長さの上限で止めると、上限を超える @ の前の頭が伏せられずに残る（Codex SO）。
  {
    pattern: /(?<![a-zA-Z0-9._%+\-])[a-zA-Z0-9._%+\-]+@[a-zA-Z0-9.\-]+\.[a-zA-Z]{2,}/g,
    replacement: '[email]',
  },
  { pattern: /(?:\+81[-\s]?|0)(?:\d{1,4}[-\s]?)(?:\d{1,4}[-\s]?)\d{3,4}/g, replacement: '[phone]' },
  { pattern: /〒?\d{3}[-ー]\d{4}/g, replacement: '[postal]' },
]

// DrizzleQueryError の message は `Failed query: <sql>\nparams: <値>`（lib/db/scrub-db-error.ts 冒頭の実測）。
// 値には氏名・住所・自由文が入り、下の正規表現では伏せられない。scrubDbError は呼び出し側が包んだときだけ
// 効くので、Sentry の全ての文字列面が通るここで落とす（AUTH-010）。
// 🔴 `params:` より前の SQL 本文も安全ではない。`sql.raw(...)` で値を埋め込んだ生 SQL はリテラルが
//    本文に残る（Codex 修正確認 HIGH）。`Failed query:` 以降は丸ごと伏せる。例外の種類は
//    exception.type に、Postgres のエラーコードは cause 側に残るので、調査の手がかりは失わない。
// 自由文の値は改行を含み得るため、区切りを探さず末尾まで落とす。
const DB_QUERY_MARKER = 'Failed query:'
const DB_PARAMS_MARKER = '\nparams:'

/** DB クエリ例外の本文（`Failed query:` / `\nparams:` 以降）を伏せる。Chat 通知の安全化でも共用する。 */
export function redactDbQueryText(value: string): string {
  const query = value.indexOf(DB_QUERY_MARKER)
  if (query !== -1) return `${value.slice(0, query)}${DB_QUERY_MARKER} [REDACTED]`
  const params = value.indexOf(DB_PARAMS_MARKER)
  return params === -1 ? value : `${value.slice(0, params)}${DB_PARAMS_MARKER} [REDACTED]`
}

// 🔴 JSON の読み取りに失敗した例外文は、読もうとした本文の抜粋を持つ（V8: `Unexpected token 'X', "<抜粋>" is not valid JSON`・
//    長い本文は抜粋の前後に `...` が付く・`"<抜粋>" is not valid JSON`）。`req.json()` の失敗をそのまま記録する経路では、受け取った本文の氏名・自由文が
//    例外文に乗って出る（2026-10-19 Codex SO HIGH・app/api/customer-box-files/route.ts の json parse 等）。
//    抜粋だけ伏せ、「JSON として読めなかった」ことは残す（位置だけの文 `... in JSON at position N` は本文を持たないので触らない）。
// 🔴 抜粋の長さに上限を付ける（V8 は本文が20文字以下なら全部・それより長ければ10文字＋`...`・実測 Node 24）。
//    上限なしの `[\s\S]*` は、引用符の多い長い文（8万文字）で1件 4.3 秒かかった（maskPii は全ての文字列が通る）。
//    目印の文が無い文字列は照合しない。
const JSON_EXCERPT_MARKER = ' is not valid JSON'
const JSON_EXCERPT_PATTERNS: ReadonlyArray<{ pattern: RegExp; replacement: string }> = [
  {
    pattern:
      /Unexpected token '[\s\S]{1,4}?', (?:\.\.\.)?"[\s\S]{0,64}?"(?:\.\.\.)? is not valid JSON/g,
    replacement: 'Unexpected token [REDACTED] is not valid JSON',
  },
  {
    pattern: /(?:\.\.\.)?"[\s\S]{0,64}?"(?:\.\.\.)? is not valid JSON/g,
    replacement: '"[REDACTED]" is not valid JSON',
  },
]

/** JSON の読み取り失敗の例外文から、本文の抜粋を伏せる。 */
export function redactJsonParseExcerpt(value: string): string {
  if (!value.includes(JSON_EXCERPT_MARKER)) return value
  return JSON_EXCERPT_PATTERNS.reduce(
    (s, { pattern, replacement }) => s.replace(pattern, replacement),
    value
  )
}

export function maskPii(value: string | null | undefined): string | undefined {
  if (value == null) return undefined
  const withoutPii = PII_PATTERNS.reduce(
    (s, { pattern, replacement }) => s.replace(pattern, replacement),
    // 🔴 JSON の抜粋を先に伏せる。DB 用は `Failed query:` 以降を切るので、抜粋の中にその文字があると
    //    目印の文 ` is not valid JSON` ごと消え、抜粋の頭（氏名など）が残る（Codex SO 3回目 HIGH）。
    redactDbQueryText(redactJsonParseExcerpt(value))
  )
  // 🔴 URL の秘密もここで落とす。maskPii は message / exception.value / extra /
  // breadcrumb.message / request.data の全ての文字列面が通る唯一の場所なので、
  // ここへ寄せると面ごとの足し忘れが起きない（Codex SO v2 の指摘）。
  return redactSecretsInUrl(withoutPii)
}

// 悪意あるオブジェクトや誤ったネストによるスタックオーバーフローを防ぐため再帰深度を 10 に制限する
export function sanitizeObject(obj: unknown, visited = new Set<object>(), depth = 0): unknown {
  if (depth > 10) return '[truncated]'
  if (obj == null) return obj // null と undefined を一括除外
  if (typeof obj === 'string') return maskPii(obj)
  if (typeof obj !== 'object') return obj // ここ以降 obj は object（null は除外済み）
  if (visited.has(obj)) return '[circular]'
  visited.add(obj)
  if (Array.isArray(obj)) return obj.map((i) => sanitizeObject(i, visited, depth + 1))
  return Object.fromEntries(
    Object.entries(obj as Record<string, unknown>).map(([k, v]) => [
      k,
      sanitizeObject(v, visited, depth + 1),
    ])
  )
}

// ─── URL 中の秘密（入口トークン）の秘匿 ────────────────────────────────────────
// お客様マイページの入口は URL に秘密を載せる。招待は使い切りだが未使用なら期限まで生き、
// 旧ポータルトークンは 1年有効・失効実績0。これらが Sentry（社外）へ残ると、
// Sentry を見られる人がそのまま使えてしまう。
//
// 🔴 **ルートの形で判定する。** 「長い英数字を全部潰す」汎用パターンは採らない。
// 案件ID・顧客IDの UUID まで潰れて障害調査が不能になり、
// 「サニタイズが強すぎて Sentry を使えなくする」という別の失敗になるため。
// 秘密を載せるルートが増えたら tests/quality/sentry-transaction-scrubbing.test.ts が落ちる。
//
// 🔴 **URL 専用の面だけでなく自由文にも当てる。** fetch 失敗の例外文や
// `logError(err, context)` の context にも URL は混ざる。後者は
// packages/shared-errors/src/index.ts:9,24 が **maskPii を通して** Sentry へ送るため、
// maskPii 自体が落とせないと経路ごと素通りする（Codex SO v2 の指摘）。
const REDACTED = '[redacted]'

/** `/customer-portal/<ここ>` が秘密**ではない**と分かっている固定セグメント。 */
const CUSTOMER_PORTAL_LITERAL_SEGMENTS = 'invite|mypage|reset-password'

/**
 * お客様の端末へのプッシュ通知の配信サービスのホスト（正規表現の部品）。
 * 配信サービスの許可リスト（app/projects/apps/customer-portal/shared/rules/customer-web-push-endpoint.ts）と揃える。
 */
const PUSH_SERVICE_HOST_SOURCE =
  '(?:fcm\\.googleapis\\.com|[a-z0-9-]+(?:\\.[a-z0-9-]+)*\\.(?:push\\.services\\.mozilla\\.com|push\\.apple\\.com|notify\\.windows\\.com))'
const PUSH_SERVICE_HOST = new RegExp(`^${PUSH_SERVICE_HOST_SOURCE}$`, 'i')
const PUSH_SERVICE_URL = new RegExp(`https://${PUSH_SERVICE_HOST_SOURCE}(?::443)?/`, 'i')

/** URL・パス・自由文のいずれにも当てられる形。文字列の途中に埋まっていても拾う。 */
const URL_SECRET_PATTERNS: Array<{ pattern: RegExp; replacement: string }> = [
  // /customer-portal/invite/<token>
  {
    pattern: /(\/customer-portal\/invite\/)[^\s/?#"'<>]+/g,
    replacement: `$1${REDACTED}`,
  },
  // /customer-portal/<token>/...（旧トークンURL経路・Task 9 で撤去予定）
  // 固定セグメントは否定先読みで除外する（/customer-portal/mypage/login を壊さない）
  {
    pattern: new RegExp(
      `(\\/customer-portal\\/)(?!(?:${CUSTOMER_PORTAL_LITERAL_SEGMENTS})(?:[/?#"'<>\\s]|$))[^\\s/?#"'<>]+`,
      'g'
    ),
    replacement: `$1${REDACTED}`,
  },
  // クエリ（?token=... / &access_token=... / 生の token=...）
  {
    pattern: /\b(token|access_token|invite_token|reset_token)=[^&\s"'<>]+/g,
    replacement: `$1=${REDACTED}`,
  },
  // LINE のご案内メールの印（14 日有効の持ち主証明）。/line/ 配下の t= は redactLineMarks（下）で伏せる。
  // LIFF が印を包んで渡す liff.state は `t%3D…` の形で印が入るので、鍵名でなく値全体を伏せる（設計書 31 03 §1-3）。
  {
    pattern: /\b(liff\.state=)[^&\s"'<>#]+/g,
    replacement: `$1${REDACTED}`,
  },
  // お客様の端末へのプッシュ通知の送り先（Web Push・2026-09-14）。パスとクエリに端末ごとの識別子を持つ。
  // 送信はサーバーの fetch なので、自動計測の breadcrumb・span に URL がそのまま載る（Codex SO 9周目 HIGH）。
  // ホストだけ残す（配信サービスの許可リスト app/projects/apps/customer-portal/shared/rules/customer-web-push-endpoint.ts と揃える）。
  {
    pattern: new RegExp(`(https://${PUSH_SERVICE_HOST_SOURCE}(?::443)?)/[^\\s"'<>]*`, 'gi'),
    replacement: `$1/${REDACTED}`,
  },
]

/**
 * URL・パス・クエリ文字列・それらを含む自由文から入口トークンを落とす。
 *
 * 正規表現だけで組んでいるのは、URL として解析できない値（例外文・ログの一部）でも
 * 同じ規則を当てるため。パーサを使うと「URL の面」でしか効かず、`logError` の context や
 * 例外文が素通りする。例外を投げる余地も無い（サニタイザが投げると beforeSend ごと落ちて
 * イベントが消える）。
 */
export function redactSecretsInUrl(value: string | undefined | null): string {
  if (!value) return value ?? ''
  const redacted = URL_SECRET_PATTERNS.reduce(
    (acc, { pattern, replacement }) => acc.replace(pattern, replacement),
    redactLineMarks(value, REDACTED) // /line/ 配下の t=（lib/sentry/line-marks.ts）
  )
  return redactAccessCodeFragments(redacted) // 「#code=」は画面と同じ読み方で伏せる（MYP-45）
}

// ─── リクエスト本文は丸ごと伏せる（2026-10-19・Codex SO HIGH） ─────────────────────────
// Sentry SDK は既定で受け取ったリクエストの本文を文字列のまま控え（`@sentry/core` 10.x
// `integrations/http/server-subscription.js:51` の `maxRequestBodySize = 'medium'`）、その要求の中で
// エラーを送ると `request.data` に、トランザクションでは span の `http.request.body.data` に載せる
// （`integrations/requestdata.js:98-101,139-140`）。`sendDefaultPii: false` では止まらない。
//
// 🔴 2段で守る: ①サーバーは本文を控えない（`sentry.server.config.ts` の `maxIncomingRequestBodySize: 'none'`）
// ②それでも本文が載って来たら、送る前に中身を全部伏せる（SDK の版上げで既定が変わっても漏らさない）。
// 以前（2026-09-14）は資格情報らしい鍵名の値だけを伏せていたが、Server Action の本文に入る氏名・住所・
// 自由文は鍵名で拾えず素通りした（2026-10-18 Codex SO HIGH）。本文は障害調査に使っていないので全部伏せる。

/** span の属性で本文が載る鍵。 */
const SPAN_BODY_KEY = 'http.request.body.data'

/** リクエスト本文を伏せる（エラーの `request.data` と span の本文の両方で使う）。中身があれば全部伏せる。 */
function redactRequestBody(data: unknown): unknown {
  return data == null ? data : REDACTED
}

function redactSpanBody(data: Record<string, unknown> | undefined): void {
  if (!data || !(SPAN_BODY_KEY in data)) return
  data[SPAN_BODY_KEY] = redactRequestBody(data[SPAN_BODY_KEY])
}

/** span の data のような「値が URL かもしれない」辞書の文字列値から、URL の秘密を落とす（in-place）。 */
function redactStringValuesInPlace(data: Record<string, unknown>): void {
  for (const key of Object.keys(data)) {
    const value = data[key]
    if (typeof value === 'string') data[key] = redactSecretsInUrl(value)
  }
}

/** span が宛先を分けて載せるキー（ホスト無しのパス・クエリ）。 */
const SPLIT_TARGET_KEYS = ['url.path', 'url.query', 'url.fragment', 'http.target', 'http.query']

/**
 * span の説明と data から宛先の秘密を落とす（in-place）。
 * 🔴 Sentry SDK は宛先を `url.full` だけでなく、ホスト無しの `url.path`・`url.query` にも分けて載せる
 *    （Codex SO 2026-09-14 10周目 HIGH）。宛先が配信サービスの span は、分けて載ったパス・クエリも伏せる。
 *    それ以外の宛先のパスは残す（障害調査の手がかりを潰さない）。
 */
function sanitizeSpanTarget(span: { description?: string; data?: Record<string, unknown> }): void {
  const data = span.data ?? {}
  const address = data['server.address'] ?? data['net.peer.name']
  const toPushService =
    (typeof address === 'string' && PUSH_SERVICE_HOST.test(address)) ||
    [span.description, ...Object.values(data)].some(
      (value) => typeof value === 'string' && PUSH_SERVICE_URL.test(value)
    )
  if (typeof span.description === 'string') span.description = redactSecretsInUrl(span.description)
  if (span.data) redactStringValuesInPlace(span.data)
  if (!toPushService) return
  for (const key of SPLIT_TARGET_KEYS) {
    if (typeof data[key] === 'string') data[key] = REDACTED
  }
  // 説明が「POST /fcm/send/<識別子>」のようにホスト無しのパスを持つ形も伏せる。
  if (typeof span.description === 'string') {
    span.description = span.description.replace(/(^|\s)\/\S*/g, `$1/${REDACTED}`)
  }
}

/** breadcrumb の data から遷移先URLを秘匿する（navigation / fetch / xhr が URL を持つ）。 */
const BREADCRUMB_URL_KEYS = new Set(['to', 'from', 'url'])
function sanitizeBreadcrumbData(data: Record<string, unknown>): Record<string, unknown> {
  const sanitized = sanitizeObject(data) as Record<string, unknown>
  for (const key of BREADCRUMB_URL_KEYS) {
    const value = sanitized[key]
    if (typeof value === 'string') sanitized[key] = redactSecretsInUrl(value)
  }
  return sanitized
}

/**
 * Sentry へ載せてよいヘッダー名（小文字で比較する・**許可リスト**）。
 *
 * SENTRY-PII-COOKIE-LEAK-1（2026-09-05）:
 * 最初は「落とすものを列挙する」形にしたが、敵対検証で**4本落とし漏らしている**と分かった
 * （x-vercel-oidc-token / x-amz-security-token 等）。秘密ヘッダーは増え続けるので、
 * 列挙方式は増えるたびに漏れる＝ fail-open。**残すものを列挙する**形に変え、
 * 知らないヘッダーは落とす（fail-closed）。
 *
 * ここに無いものが要るようになったら、その都度「載せてよい理由」とともに足す。
 * 障害調査で実際に使うものだけを入れること。
 */
const SAFE_HEADER_NAMES = new Set([
  'user-agent',
  'content-type',
  'content-length',
  'accept',
  'accept-language',
  'accept-encoding',
  'host',
  'referer',
  'origin',
  'x-request-id',
  'x-vercel-id',
  'sentry-trace',
  'baggage',
])

/**
 * Sentry Logs（`beforeSendLog`）へ出す記録の本文と属性を伏せる。🔴 Logs は beforeSend を通らない（公式: options の
 * beforeSendLog）。warning を 2026-09-27 に Logs へ移したので、繋がないとメール・電話・住所・DB の値が素のまま出る。
 * 伏せ方は errors と同じ maskPii / sanitizeObject（別の規則を作らない）。
 */
export function sanitizeLogForPii(log: Log): Log {
  const message = typeof log.message === 'string' ? log.message : String(log.message)
  return {
    ...log,
    message: maskPii(message) ?? '',
    ...(log.attributes
      ? { attributes: sanitizeObject(log.attributes) as Record<string, unknown> }
      : {}),
  }
}

/**
 * AI の SDK の自動計測が送った例外の印（mechanism.type の頭）。
 * 🔴 Sentry の既定の integration（anthropicAIIntegration・googleGenAIIntegration）は、AI の応答の途中で起きた
 *    例外を、呼び出し側が本文を消す前の SDK の例外のまま captureException する（@sentry/core の
 *    tracing/anthropic-ai/streaming.js「auto.ai.anthropic.stream_error」・google-genai「auto.ai.google_genai」）。
 *    SDK の例外の文言には応答の本文（議事録・お客様の会話）が入り得て、maskPii（メール・電話・郵便）では伏せきれない。
 *    種類（exception.type）と印は残し、文言だけを丸ごと伏せる（Codex SO 2026-10-23 HIGH・Claude 切り替え便1）。
 */
const AI_SDK_MECHANISM_PREFIX = 'auto.ai.'
export const AI_SDK_EXCEPTION_REDACTED = '[AI の SDK の例外の文言は伏せた]'

export function sanitizeEventForPii(event: Event, _hint?: EventHint): Event {
  if (event.user) {
    event.user = { id: event.user.id }
  }
  if (event.exception?.values) {
    // 🔴 1つでも AI の SDK の印が付いていたら、つながっている例外（cause・LinkedErrors の mechanism=chained）も
    //    全部伏せる。親だけ伏せると、cause の TypeError（鍵の入った見出しの値など）が残る（Codex SO 2026-10-23 4周目）
    const fromAiSdk = event.exception.values.some((ex) =>
      ex.mechanism?.type?.startsWith(AI_SDK_MECHANISM_PREFIX)
    )
    event.exception.values = event.exception.values.map((ex) => ({
      ...ex,
      value: fromAiSdk ? AI_SDK_EXCEPTION_REDACTED : maskPii(ex.value),
    }))
  }
  if (event.message) event.message = maskPii(event.message)
  if (event.extra) event.extra = sanitizeObject(event.extra) as Record<string, unknown>
  if (event.breadcrumbs) {
    event.breadcrumbs = event.breadcrumbs.map((bc) => ({
      ...bc,
      message: maskPii(bc.message),
      data: bc.data ? sanitizeBreadcrumbData(bc.data) : bc.data,
    }))
  }
  if (event.request?.data) {
    event.request.data = redactRequestBody(event.request.data)
  }
  const traceData = (event.contexts?.trace as { data?: Record<string, unknown> } | undefined)?.data
  redactSpanBody(traceData)
  for (const span of (event.spans ?? []) as Array<{ data?: Record<string, unknown> }>) {
    redactSpanBody(span.data)
  }
  if (event.request?.url) event.request.url = redactSecretsInUrl(event.request.url)
  if (event.request?.query_string && typeof event.request.query_string === 'string') {
    event.request.query_string = redactSecretsInUrl(event.request.query_string)
    // query_string にはパスが無いので、url が /line/ 配下のときだけ t= を伏せる（LINE のご案内メールの印）
    if (/\/line\//.test(event.request.url ?? '')) {
      event.request.query_string = event.request.query_string.replace(
        /(^|&)(t=)[^&]+/g,
        `$1$2${REDACTED}`
      )
    }
  }
  if (event.transaction) event.transaction = redactSecretsInUrl(event.transaction)
  // 🔴 トランザクションの子 span（サーバーからの fetch 等）は説明と data に宛先 URL を持つ。
  if (event.spans) {
    for (const span of event.spans) sanitizeSpanTarget(span)
  }
  if (event.contexts?.trace?.data) sanitizeSpanTarget({ data: event.contexts.trace.data })
  if (event.request?.headers) {
    // @sentry/core RequestEventData['headers'] の型は { [key: string]: string }（= Record<string, string>）
    const h = event.request.headers as Record<string, string | undefined>
    for (const key of Object.keys(h)) {
      if (!SAFE_HEADER_NAMES.has(key.toLowerCase())) {
        delete h[key]
        continue
      }
      // 許可したヘッダーでも、値に秘密が乗るものは伏せる（referer は招待 URL を運ぶ）
      const value = h[key]
      if (typeof value === 'string') h[key] = redactSecretsInUrl(value)
    }
  }
  // 🔴 headers.cookie を消すだけでは足りない。@sentry/core 10.x は
  // integrations/requestdata.js:133-134 で `requestData.cookies` に**パース済み辞書を別枠で**
  // 載せる（sendDefaultPii:false でも include.cookies は true）。ここを消さないと
  // スタッフのセッショントークンが出続ける（SENTRY-PII-COOKIE-LEAK-1・2026-09-05 実測）。
  if (event.request && 'cookies' in event.request) {
    delete (event.request as { cookies?: unknown }).cookies
  }
  return event
}
